/**
 * 播放器历史记录管理器
 * 负责在播放器场景下自动保存和上报播放历史记录
 */

import { OpType } from '@/types/common';
import { usePlayerStore } from '@/stores/playerStore';
import { useHistoryStore } from '@/stores/history';
import { HistorySaveItem } from '@/types/history';
import { VideoType } from '@/pages/playDetail/types';
import { logger } from '@/utils/devUtils';

/**
 * 播放历史记录管理器配置
 */
interface PlayerHistoryConfig {
  /** 自动保存间隔（毫秒），默认 10 秒 */
  saveInterval?: number;
  /** 自动上报间隔（毫秒），默认 60 秒（1分钟） */
  reportInterval?: number;
  /** 最小播放时长（秒），低于此值不上报，默认 10 秒 */
  minPlayDuration?: number;
  /** 播放进度变化阈值（秒），超过此值才保存，默认 3 秒 */
  progressThreshold?: number;
  /** 拖动进度条上报阈值（秒），拖动距离超过此值才上报，默认 60 秒 */
  seekReportThreshold?: number;
}

/**
 * 播放历史记录管理器
 */
class PlayerHistoryManager {
  private static instance: PlayerHistoryManager;

  /**
   * 获取单例实例
   */
  static getInstance(config?: PlayerHistoryConfig): PlayerHistoryManager {
    if (!PlayerHistoryManager.instance) {
      PlayerHistoryManager.instance = new PlayerHistoryManager(config);
    }
    return PlayerHistoryManager.instance;
  }

  private saveTimer: NodeJS.Timeout | null = null;
  private reportTimer: NodeJS.Timeout | null = null;
  private lastSavedTime = 0;
  private lastReportedTime = 0;
  private enabled = true; // 是否启用历史记录
  private config: Required<PlayerHistoryConfig>;
  private hasReportedOnSeek = false; // 是否已在拖动时上报过
  private lastPlayModeWasFullscreen = false; // 当前/上次播放是否为全屏模式（仅全屏才上报退出）

  private constructor(config: PlayerHistoryConfig = {}) {
    this.config = {
      saveInterval: config.saveInterval ?? 10000, // 10秒保存一次
      reportInterval:
        parseInt(localStorage.getItem('player_report_gap') ?? '60', 10) * 1000 || config.reportInterval || 60000, // 60秒（1分钟）上报一次
      minPlayDuration: config.minPlayDuration ?? 10, // 至少播放10秒才上报
      progressThreshold: config.progressThreshold ?? 3, // 进度变化超过3秒才保存
      seekReportThreshold: config.seekReportThreshold ?? 60, // 拖动距离超过60秒才上报
    };
  }

  /**
   * 初始化管理器
   * 监听播放器事件，自动保存和上报历史记录
   */
  init() {
    // 监听播放状态变化
    if (typeof window !== 'undefined') {
      window.addEventListener('player:statechange', this.handleStateChange as EventListener);
      window.addEventListener('player:timeupdate', this.handleTimeUpdate as EventListener);
    }

    logger.log('[PlayerHistoryManager] 初始化完成');
  }

  /**
   * 销毁管理器
   */
  destroy() {
    this.stopTimers();
    if (typeof window !== 'undefined') {
      window.removeEventListener('player:statechange', this.handleStateChange as EventListener);
      window.removeEventListener('player:timeupdate', this.handleTimeUpdate as EventListener);
    }
    logger.log('[PlayerHistoryManager] 已销毁');
  }

  /**
   * 设置是否启用历史记录
   */
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    if (!enabled) {
      // 禁用时停止所有定时器
      this.stopTimers();
    }
    logger.log('[PlayerHistoryManager] 历史记录已', enabled ? '启用' : '禁用');
  }

  /**
   * 获取是否启用历史记录
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * 设置当前播放是否为全屏模式（仅全屏时退出才上报历史）
   */
  setLastPlayModeFullscreen(fullscreen: boolean) {
    this.lastPlayModeWasFullscreen = fullscreen;
  }

  /**
   * 获取当前/上次播放是否为全屏模式
   */
  isLastPlayModeFullscreen(): boolean {
    return this.lastPlayModeWasFullscreen;
  }

  /**
   * 手动保存历史记录（供外部调用）
   */
  async saveHistoryManually(cid: string, vid: string) {
    await this.saveHistory(cid, vid, true);
  }

  /**
   * 手动上报历史记录（供外部调用）
   */
  async reportHistoryManually(cid: string, vid: string) {
    await this.reportHistory(cid, vid);
  }

  /**
   * 处理拖动进度条结束（供外部调用）
   * 根据流程图：如果拖动距离>60秒，拖动结束时上报
   */
  async handleSeekEnd(cid: string, vid: string, seekStartTime: number, seekEndTime: number) {
    if (!cid || !vid) return;

    const seekDistance = Math.abs(seekEndTime - seekStartTime);

    // 如果拖动距离超过阈值，且还未上报过，则上报
    if (seekDistance >= this.config.seekReportThreshold && !this.hasReportedOnSeek) {
      try {
        await this.reportHistory(cid, vid);
        this.hasReportedOnSeek = true;
        logger.log('[PlayerHistoryManager] 拖动进度条结束，已上报', {
          cid,
          vid,
          seekDistance,
          seekStartTime,
          seekEndTime,
        });
      } catch (error) {
        logger.error('[PlayerHistoryManager] 拖动结束时上报失败:', error);
      }
    }
  }

  /**
   * 退出播放页面时上报（供外部调用）
   */
  async reportOnExit(cid: string, vid: string) {
    if (!cid || !vid) return;

    // 退出播放页面时立即上报
    try {
      await this.reportHistory(cid, vid);
      logger.log('[PlayerHistoryManager] 退出播放页面，已上报', { cid, vid });
    } catch (error) {
      logger.error('[PlayerHistoryManager] 退出播放页面时上报失败:', error);
    }
  }

  /**
   * 处理播放状态变化
   */
  private handleStateChange = (event: Event) => {
    if (!this.enabled) return;

    const customEvent = event as CustomEvent;
    const state = customEvent.detail?.data?.new;
    const playerState = usePlayerStore.getState();
    console.log('handleStateChange', state, playerState);
    if (state === 'playing') {
      // 开始播放
      this.onPlayStart(playerState.cid, playerState.vid);
    } else if (state === 'pause') {
      // 暂停时立即保存和上报
      this.onPause(playerState.cid, playerState.vid);
    } else if (state === 'error') {
      // 播放错误时保存当前进度
      this.onError(playerState.cid, playerState.vid);
    } else if (state === 'idle') {
      // 播放结束（idle 状态）时保存当前进度
      this.onPlayEnd(playerState.cid, playerState.vid);
    }
  };

  /**
   * 处理时间更新
   */
  private handleTimeUpdate = (event: Event) => {
    if (!this.enabled) return;

    const customEvent = event as CustomEvent;
    const time = customEvent.detail?.time || 0;
    const playerState = usePlayerStore.getState();
    if (playerState.cid && playerState.vid && time > 0) {
      this.onTimeUpdate(playerState.cid, playerState.vid, time);
    }
  };

  /**
   * 播放开始
   */
  private onPlayStart(cid: string | null, vid: string | null) {
    if (!cid || !vid) return;
    this.hasReportedOnSeek = false;
    // 启动定时保存
    this.startSaveTimer();
    // 启动定时上报（1分钟间隔）
    this.startReportTimer();

    // 起播后立即上报（根据流程图要求）
    this.reportHistory(cid, vid).catch((error) => {
      logger.error('[PlayerHistoryManager] 起播时上报失败:', error);
    });
  }

  /**
   * 播放暂停
   */
  private onPause(cid: string | null, vid: string | null) {
    if (!cid || !vid) return;

    // 立即保存和上报
    this.saveHistory(cid, vid, true);
  }

  /**
   * 播放错误
   */
  private onError(cid: string | null, vid: string | null) {
    if (!cid || !vid) return;

    // 保存当前进度
    this.saveHistory(cid, vid, true);
  }

  /**
   * 播放结束
   */
  private onPlayEnd(cid: string | null, vid: string | null) {
    if (!cid || !vid) return;

    // 停止定时器
    this.stopTimers();

    // 保存当前进度（播放结束时立即保存和上报）
    this.saveHistory(cid, vid, true).catch((error) => {
      logger.error('[PlayerHistoryManager] 播放结束时保存失败:', error);
    });
    this.reportHistory(cid, vid).catch((error) => {
      logger.error('[PlayerHistoryManager] 播放结束时上报失败:', error);
    });
  }

  /**
   * 时间更新（实际保存由定时器触发，此处仅做校验）
   */
  private onTimeUpdate(cid: string | null, vid: string | null, _currentTime: number) {
    if (!cid || !vid) return;
  }

  /**
   * 启动保存定时器
   */
  private startSaveTimer() {
    this.stopSaveTimer();
    this.saveTimer = setInterval(() => {
      const playerState = usePlayerStore.getState();
      if (playerState.cid && playerState.vid) {
        this.saveHistory(playerState.cid, playerState.vid, false);
      }
    }, this.config.saveInterval);
  }

  /**
   * 停止保存定时器
   */
  private stopSaveTimer() {
    if (this.saveTimer) {
      clearInterval(this.saveTimer);
      this.saveTimer = null;
    }
  }

  /**
   * 启动上报定时器
   */
  private startReportTimer() {
    this.stopReportTimer();
    this.reportTimer = setInterval(() => {
      const playerState = usePlayerStore.getState();
      if (playerState.cid && playerState.vid) {
        this.reportHistory(playerState.cid, playerState.vid);
      }
    }, this.config.reportInterval);
  }

  /**
   * 停止上报定时器
   */
  private stopReportTimer() {
    if (this.reportTimer) {
      clearInterval(this.reportTimer);
      this.reportTimer = null;
    }
  }

  /**
   * 停止所有定时器
   */
  private stopTimers() {
    this.stopSaveTimer();
    this.stopReportTimer();
  }

  /**
   * 保存历史记录到本地
   */
  private async saveHistory(cid: string, vid: string, immediate = false) {
    if (!this.enabled) return;

    try {
      const playerState = usePlayerStore.getState();
      const { currentTime, totalTime } = playerState;

      // 检查是否有有效数据
      if (!cid || !vid || totalTime <= 0) {
        return;
      }

      // 检查播放时长是否足够
      if (currentTime < this.config.minPlayDuration && !immediate) {
        return;
      }

      // 检查进度是否变化（避免重复保存）
      if (!immediate && Math.abs(currentTime - this.lastSavedTime) < this.config.progressThreshold) {
        return;
      }

      // 构建历史记录项（保证播放时长不超过总时长）
      const totalTimeFloored = Math.floor(totalTime);
      const historyItem: HistorySaveItem = {
        cid,
        vid,
        modifyTime: `${Date.now()}`,
        videoPlayTime: Math.min(Math.floor(currentTime), totalTimeFloored),
        videoTotalTime: totalTimeFloored,
        type: 1, // 全部按照长视频处理，后续上报历史记录时候会判断
      };

      // 更新历史记录（保存到本地存储）
      await useHistoryStore.getState().updateHistory(historyItem, OpType.OpTypeUpdate);

      this.lastSavedTime = currentTime;
      logger.log('[PlayerHistoryManager] 历史记录已保存', {
        cid,
        vid,
        playTime: currentTime,
        totalTime,
      });
    } catch (error) {
      logger.error('[PlayerHistoryManager] 保存历史记录失败:', error);
    }
  }

  /**
   * 上报历史记录到服务器
   */
  private async reportHistory(cid: string, vid: string) {
    if (!this.enabled) return;

    try {
      const playerState = usePlayerStore.getState();
      const { currentTime, totalTime } = playerState;

      // 检查是否有有效数据
      if (!cid || !vid || totalTime <= 0) {
        return;
      }

      // 检查播放时长是否足够
      if (currentTime < this.config.minPlayDuration) {
        return;
      }

      // 检查是否需要上报（避免频繁上报）
      if (Math.abs(currentTime - this.lastReportedTime) < this.config.progressThreshold) {
        return;
      }

      // 构建历史记录项（保证播放时长不超过总时长）
      const totalTimeFloored = Math.floor(totalTime);
      const historyItem: HistorySaveItem = {
        cid,
        vid,
        modifyTime: `${Date.now()}`,
        videoPlayTime: Math.min(Math.floor(currentTime), totalTimeFloored),
        videoTotalTime: totalTimeFloored,
        type: 1, // 全部按照长视频处理，后续上报历史记录时候会判断
      };

      // 更新历史记录（会自动上报到服务器，如果是登录状态）
      await useHistoryStore.getState().updateHistory(historyItem, OpType.OpTypeUpdate);

      this.lastReportedTime = currentTime;
      logger.log('[PlayerHistoryManager] 历史记录已上报', {
        cid,
        vid,
        playTime: currentTime,
        totalTime,
      });
    } catch (error) {
      logger.error('[PlayerHistoryManager] 上报历史记录失败:', error);
    }
  }

  /**
   * 获取视频类型
   */
  private getVideoType(videoInfo: any): VideoType {
    if (!videoInfo) {
      return VideoType.LONG; // 默认长视频
    }

    // 根据 videoInfo 判断视频类型
    // 优先从 videoInfo.type 或 parseData.type 获取
    const type = videoInfo.type || videoInfo.parseData?.type;
    if (type !== undefined && type !== null) {
      // 确保返回的是 VideoType 枚举值
      if (typeof type === 'number') {
        return type as VideoType;
      }
      // 如果是字符串，尝试转换
      if (typeof type === 'string') {
        const typeNum = parseInt(type, 10);
        if (!isNaN(typeNum)) {
          return typeNum as VideoType;
        }
      }
    }

    // 根据其他字段判断
    // 例如：根据 duration 判断（短视频通常小于某个时长）
    const duration = videoInfo.duration || videoInfo.parseData?.duration;
    if (duration && duration < 600) {
      // 小于10分钟认为是短视频
      return VideoType.SHORT;
    }

    return VideoType.LONG; // 默认长视频
  }
}

/**
 * 获取播放历史记录管理器实例
 */
export function getPlayerHistoryManager(config?: PlayerHistoryConfig): PlayerHistoryManager {
  return PlayerHistoryManager.getInstance(config);
}

/**
 * 设置播放历史记录是否启用
 */
export function setPlayerHistoryEnabled(enabled: boolean): void {
  const manager = getPlayerHistoryManager();
  manager.setEnabled(enabled);
}

/**
 * 设置当前播放是否为全屏模式（仅全屏时退出才上报历史）
 */
export function setPlayerHistoryLastPlayModeFullscreen(fullscreen: boolean): void {
  getPlayerHistoryManager().setLastPlayModeFullscreen(fullscreen);
}

/**
 * 初始化播放历史记录管理器（供外部调用）
 */
export function initPlayerHistoryManager(config?: PlayerHistoryConfig): PlayerHistoryManager {
  const manager = getPlayerHistoryManager(config);
  manager.init();
  return manager;
}
